import { OrganizationUser } from '@app/v2/shared/types/user.type'
import { USER_STATUS_ACTION } from './status-dropdown-options'

export type TeamMemberApiAction = 'suspend' | 'remove' | 'activate' | 'reinvite'

export type TeamMemberActionPayload = {
  id: number
  action: TeamMemberApiAction
  notify?: boolean
}

export const TEAM_MEMBER_API_ACTION: Partial<Record<USER_STATUS_ACTION, TeamMemberApiAction>> = {
  [USER_STATUS_ACTION.SUSPEND]: 'suspend',
  [USER_STATUS_ACTION.REMOVE]: 'remove',
  [USER_STATUS_ACTION.UNSUSPEND]: 'activate',
  [USER_STATUS_ACTION.CANCEL_INVITATION]: 'remove',
  [USER_STATUS_ACTION.RESEND_INVITATION]: 'reinvite'
}

export const TEAM_MEMBER_ACTIONS_REQUIRING_CONFIRMATION = new Set<USER_STATUS_ACTION>([
  USER_STATUS_ACTION.SUSPEND,
  USER_STATUS_ACTION.REMOVE,
  USER_STATUS_ACTION.UNSUSPEND,
  USER_STATUS_ACTION.CANCEL_INVITATION
])
/**
 * @description Escapes HTML special characters in user-controlled text
 * @param {string} value Raw text
 * @returns {string} Escaped text safe for HTML interpolation
 */
const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

/**
 * @description Styles for blue action buttons
 * @type {string}
 */
const BLUE_BTN = 'tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-ring-blue-300'
const RED_BTN = 'tw-bg-red-600 hover:tw-bg-red-700 focus:tw-ring-red-300'
const MEMBER_NAME = (name: string) => `<span class="tw-font-semibold">${escapeHtml(name)}</span>`
const TRASH_ICON = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_remove_header)"><path d="M12.7044 2.94737H9.8525V1.47368C9.8525 1.08284 9.70227 0.708001 9.43486 0.431632C9.16745 0.155263 8.80476 0 8.42658 0H5.57473C5.19655 0 4.83386 0.155263 4.56644 0.431632C4.29903 0.708001 4.1488 1.08284 4.1488 1.47368V2.94737H1.29695C1.10786 2.94737 0.926513 3.025 0.792806 3.16318C0.6591 3.30137 0.583984 3.48879 0.583984 3.68421C0.583984 3.87963 0.6591 4.06705 0.792806 4.20524C0.926513 4.34342 1.10786 4.42105 1.29695 4.42105H2.00991V12.5263C2.00991 12.9172 2.16014 13.292 2.42755 13.5684C2.69497 13.8447 3.05766 14 3.43584 14H10.5655C10.9436 14 11.3063 13.8447 11.5737 13.5684C11.8412 13.292 11.9914 12.9172 11.9914 12.5263V4.42105H12.7044C12.8934 4.42105 13.0748 4.34342 13.2085 4.20524C13.3422 4.06705 13.4173 3.87963 13.4173 3.68421C13.4173 3.48879 13.3422 3.30137 13.2085 3.16318C13.0748 3.025 12.8934 2.94737 12.7044 2.94737ZM5.57473 1.47368H8.42658V2.94737H5.57473V1.47368ZM6.28769 11.0526C6.28769 11.2481 6.21257 11.4355 6.07887 11.5737C5.94516 11.7118 5.76381 11.7895 5.57473 11.7895C5.38564 11.7895 5.20429 11.7118 5.07058 11.5737C4.93688 11.4355 4.86176 11.2481 4.86176 11.0526V5.89474C4.86176 5.69931 4.93688 5.5119 5.07058 5.37371C5.20429 5.23553 5.38564 5.15789 5.57473 5.15789C5.76381 5.15789 5.94516 5.23553 6.07887 5.37371C6.21257 5.5119 6.28769 5.69931 6.28769 5.89474V11.0526ZM9.13954 11.0526C9.13954 11.2481 9.06442 11.4355 8.93072 11.5737C8.79701 11.7118 8.61567 11.7895 8.42658 11.7895C8.23749 11.7895 8.05614 11.7118 7.92244 11.5737C7.78873 11.4355 7.71361 11.2481 7.71361 11.0526V5.89474C7.71361 5.69931 7.78873 5.5119 7.92244 5.37371C8.05614 5.23553 8.23749 5.15789 8.42658 5.15789C8.61567 5.15789 8.79701 5.23553 8.93072 5.37371C9.06442 5.5119 9.13954 5.69931 9.13954 5.89474V11.0526Z" fill="#111928"/></g><defs><clipPath id="clip0_remove_header"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>`
const LOCK_ICON = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_suspend_header)"><path d="M13.5003 5.6H12.1253V3.6C12.1253 2.64522 11.6907 1.72955 10.9171 1.05442C10.1436 0.379285 9.09434 0 8.00033 0C6.90631 0 5.8571 0.379285 5.08351 1.05442C4.30992 1.72955 3.87533 2.64522 3.87533 3.6V5.6H2.50033C2.0141 5.6 1.54778 5.76857 1.20396 6.06863C0.860146 6.36869 0.666992 6.77565 0.666992 7.2V14.4C0.666992 14.8243 0.860146 15.2313 1.20396 15.5314C1.54778 15.8314 2.0141 16 2.50033 16H13.5003C13.9866 16 14.4529 15.8314 14.7967 15.5314C15.1405 15.2313 15.3337 14.8243 15.3337 14.4V7.2C15.3337 6.77565 15.1405 6.36869 14.7967 6.06863C14.4529 5.76857 13.9866 5.6 13.5003 5.6ZM8.91699 12C8.91699 12.2122 8.82042 12.4157 8.64851 12.5657C8.4766 12.7157 8.24344 12.8 8.00033 12.8C7.75721 12.8 7.52405 12.7157 7.35214 12.5657C7.18024 12.4157 7.08366 12.2122 7.08366 12V9.6C7.08366 9.38783 7.18024 9.18434 7.35214 9.03432C7.52405 8.88429 7.75721 8.8 8.00033 8.8C8.24344 8.8 8.4766 8.88429 8.64851 9.03432C8.82042 9.18434 8.91699 9.38783 8.91699 9.6V12ZM10.292 5.6H5.70866V3.6C5.70866 3.06957 5.9501 2.56086 6.37987 2.18579C6.80964 1.81071 7.39254 1.6 8.00033 1.6C8.60811 1.6 9.19101 1.81071 9.62078 2.18579C10.0505 2.56086 10.292 3.06957 10.292 3.6V5.6Z" fill="#111928"/></g><defs><clipPath id="clip0_suspend_header"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>`

export type TeamMemberActionUi = {
  header: string
  headerIcon?: string
  confirmLabel: string
  confirmBtnClass: string
  message: (name: string) => string
  actionNoun?: 'suspension' | 'removal'
}

const TEAM_MEMBER_ACTION_UI: Partial<Record<USER_STATUS_ACTION, TeamMemberActionUi>> = {
  [USER_STATUS_ACTION.SUSPEND]: {
    header: 'Suspend team member',
    headerIcon: LOCK_ICON,
    confirmLabel: 'Suspend',
    confirmBtnClass: BLUE_BTN,
    message: (name) => `Are you sure you want to suspend ${MEMBER_NAME(name)}?`,
    actionNoun: 'suspension'
  },
  [USER_STATUS_ACTION.REMOVE]: {
    header: 'Remove team member',
    headerIcon: TRASH_ICON,
    confirmLabel: 'Remove',
    confirmBtnClass: RED_BTN,
    message: (name) => `Are you sure you want to remove ${MEMBER_NAME(name)} from your team?`,
    actionNoun: 'removal'
  },
  [USER_STATUS_ACTION.UNSUSPEND]: {
    header: 'Unsuspend team member',
    confirmLabel: 'Unsuspend',
    confirmBtnClass: BLUE_BTN,
    message: (name) => `Are you sure you want to unsuspend ${MEMBER_NAME(name)}?`
  },
  [USER_STATUS_ACTION.CANCEL_INVITATION]: {
    header: 'Cancel invitation',
    confirmLabel: 'Cancel invitation',
    confirmBtnClass: RED_BTN,
    message: (name) => `Are you sure you want to cancel the invitation for ${MEMBER_NAME(name)}?`
  }
}

/**
 * @description Returns UI copy/styles for a team member confirmation action
 * @param {USER_STATUS_ACTION} action Action being confirmed
 * @returns {TeamMemberActionUi | undefined} Action UI config
 */
export function getTeamMemberActionUi(action: USER_STATUS_ACTION): TeamMemberActionUi | undefined {
  return TEAM_MEMBER_ACTION_UI[action]
}
